import { createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { RootState } from "../../store";
import { BudgetPeriod, cloudChiprApi } from "../../../services/cloudchipr.api";
import { resourceExplorerDataByViewIdFixedCacheKeyGenerator } from "../selectors/current-budget/chart/resourceExplorerDataByViewIdSelector";
import { currentBudgetViewIdSelector } from "../selectors/current-budget/data-selectors/currentBudgetViewIdSelector";
import { getResourceExplorerViewByIdThunk } from "../../common/thunks/resource-explorer/getResourceExplorerViewByIdThunk";
import { formDataSelector } from "../selectors/current-budget/forms/formDataSelector";
import { resourceExplorerDefaultData } from "@/components/pages/resource-explorer/components/resource-explorer-card/utils/constants/common";
import { enqueueSnackbarErrorAlert } from "@/components/pages/common/snackbar-alert/EnqueueSnackbarErrorAlert";
import {
  formatDate,
  MomentDateTimeUnitsType,
} from "@/utils/helpers/date-time/datetime-format";
import { budgetPeriodUnits } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/budgets/components/create-budget/utils/constants/date-selection";
import { currentBudgetFilterTreeSelector } from "@/store/budgets/selectors/current-budget/data-selectors/currentBudgetFilterTreeSelector";
import { currentBudgetBasedOnSelector } from "@/store/budgets/selectors/current-budget/data-selectors/currentBudgetBasedOnSelector";

export const getResourceExplorerDataByBudgetThunk = createAsyncThunk(
  "budgets/getResourceExplorerDataByBudget",
  async (_: void, { dispatch, getState }) => {
    const state = getState() as RootState;
    const viewId = currentBudgetViewIdSelector(state);
    const filterTree = currentBudgetFilterTreeSelector(state) ?? undefined;
    const basedOn = currentBudgetBasedOnSelector(state);
    const { postUsersMeOrganisationsCurrentResourceExplorer } =
      cloudChiprApi.endpoints;

    const budgetData = formDataSelector(state);

    if (!viewId) {
      return;
    }

    const response = await dispatch(
      getResourceExplorerViewByIdThunk({ viewId }),
    ).unwrap();

    if (!response) {
      return;
    }

    const budgetPeriod = budgetData?.period;
    const dataPoint =
      budgetPeriod === "daily" || budgetPeriod === "custom"
        ? "daily"
        : "monthly";

    const unit = getBudgetPeriodUnit(budgetPeriod);
    const dateTo = formatDate(moment(), { type: "yearMonthDay" });

    const subtractedDate = moment().subtract(5, unit);

    const dateFrom = formatDate(
      budgetPeriod && dateFromStartOfMonth.includes(budgetPeriod)
        ? subtractedDate.startOf("month")
        : subtractedDate,
      {
        type: "yearMonthDay",
      },
    );

    try {
      await dispatch(
        postUsersMeOrganisationsCurrentResourceExplorer.initiate(
          {
            resourceExplorerFilterSchema: {
              group_by:
                response?.data?.grouping ??
                resourceExplorerDefaultData.grouping,
              group_values: response?.data?.group_values,
              data_point: dataPoint,
              date_to: dateTo,
              date_from: dateFrom,
              filter_tree:
                basedOn === "viewId" ? response?.filter_tree : filterTree,
            },
          },
          {
            fixedCacheKey:
              resourceExplorerDataByViewIdFixedCacheKeyGenerator(viewId),
          },
        ),
      );
    } catch (e) {
      // @ts-expect-error todo: fix api spec;
      enqueueSnackbarErrorAlert(e?.data?.message);
    }
  },
);

const getBudgetPeriodUnit = (period: BudgetPeriod | undefined) => {
  if (period === "custom") {
    return "day";
  }

  return period
    ? (budgetPeriodUnits[period] as MomentDateTimeUnitsType)
    : "month";
};

const dateFromStartOfMonth: BudgetPeriod[] = [
  "monthly",
  "quarterly",
  "annually",
];
